import {
  OpportunitiesResponse,
  OpportunitiesQueryParams,
  AddOpportunityPayload,
  AddOpportunityResponse,
  ReassignOpportunitiesPayload,
  ReassignOpportunitiesResponse,
  PatchOpportunityPayload,
  PatchOpportunityResponse,
  SearchLeadsQueryParams,
  SearchLeadsResponse,
  SearchLeadsV2QueryParams,
  SearchLeadsV2Response,
  LeadCountsResponse,
  PatientOpportunityListResponse,
  ContactOpportunityListResponse,
  FilterOpportunitiesParams,
  FilterOpportunitiesResponse,
  FilterOpportunityRow,
  Opportunity,
  OpportunityTypeItem,
  OpportunityTypesResponse,
} from "../Interfaces/OpportunitiesInterface";
import type {
  OpportunityAssigneesResponse,
  OpportunityAssigneeItem,
} from "../Interfaces/LeadDetailsInterface";
import type { Assignee } from "../types/leadDetails";
import { apiClient } from "./auth";
import {
  displaySourceChannel,
  expandChannelFilterValuesForApi,
} from "../constants/leadSourceOptions";
import {
  toApiDateRangeEnd,
  toApiDateRangeStart,
} from "../utils/dateFilterUtils";
import type { LeadCountsParams } from "../components/AgentPages/MyLeads/utils/mapOpportunitiesFiltersToLeadCountsParams";

export type { LeadCountsParams };

export interface OpportunityDetail {
  id: string;
  displayName: string;
  patientId?: string | null;
  contactId?: string | null;
  type?: string;
  leadSource?: string;
  sourceChannel?: string;
  primaryFacilityId?: string;
  departmentId?: string;
  advisedByDoctorId?: string;
}

export interface OpportunityDetailResponse {
  success?: boolean;
  data?: OpportunityDetail;
  opportunity?: OpportunityDetail;
}

/** Unwrap GET /opportunities/:id — DTO is returned bare; create uses { opportunity }. */
export function unwrapOpportunityDetail(
  body: OpportunityDetail | OpportunityDetailResponse | null | undefined
): OpportunityDetail | null {
  if (!body) return null;
  if ("opportunity" in body && body.opportunity?.id) {
    return body.opportunity;
  }
  if (
    "data" in body &&
    body.data &&
    typeof body.data === "object" &&
    typeof body.data.id === "string"
  ) {
    return body.data;
  }
  if ("id" in body && typeof body.id === "string") {
    return body;
  }
  return null;
}

/**
 * Create a new opportunity for a patient.
 * POST /opportunities/
 */
export async function addOpportunityApi(
  payload: AddOpportunityPayload
): Promise<AddOpportunityResponse> {
  const response = await apiClient.post<
    AddOpportunityPayload,
    AddOpportunityResponse
  >("/opportunities/", payload);
  return response.data;
}

/**
 * Reassign opportunities to another user.
 * POST /opportunities/reassign
 */
export async function reassignOpportunitiesApi(
  payload: ReassignOpportunitiesPayload
): Promise<ReassignOpportunitiesResponse> {
  const response = await apiClient.post<
    ReassignOpportunitiesPayload,
    ReassignOpportunitiesResponse
  >("/opportunities/reassign", payload);
  return response.data;
}

/** Bulk update opportunities (CAR-1029). POST /opportunities/bulk-update */
export interface BulkUpdateOpportunitiesPayload {
  idempotencyKey?: string;
  opportunityIds: string[];
  action: string;
  reassign?: {
    toUserId?: string;
    routingGroupId?: string;
    reason: string;
  };
  disposition?: {
    byType: Record<
      string,
      {
        dispositionId: string;
        subDispositionId: string;
        status: string;
        stageId: string;
        connectivityStatus?: "TRUE_CONNECT";
      }
    >;
  };
  status?: {
    byType: Record<string, { status: string; stageId: string }>;
  };
  stage?: {
    byType: Record<string, { stageId: string }>;
  };
  connectivity?: {
    remarkId: string;
    remarkLabel: string;
  };
}

export interface BulkUpdateOpportunitiesResultItem {
  opportunityId: string;
  opportunityNumber?: string;
  displayName?: string;
  success: boolean;
  skipped?: boolean;
  reason?: string;
}

export interface BulkUpdateSubmitResponse {
  jobId: string;
  status: "queued";
  message?: string;
}

export type BulkUpdateJobStatus = "waiting" | "active" | "completed" | "failed";

export interface BulkUpdateJobResult {
  success: boolean;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  results: BulkUpdateOpportunitiesResultItem[];
}

export interface BulkUpdateJobStatusResponse {
  jobId: string;
  status: BulkUpdateJobStatus;
  result?: BulkUpdateJobResult;
  error?: string;
}

/** @deprecated Use submitBulkUpdateJobApi + pollBulkUpdateJob instead */
export interface BulkUpdateOpportunitiesResponse extends BulkUpdateJobResult {}

/** Submit async bulk update job. POST /opportunities/bulk-update → 202 */
export async function submitBulkUpdateJobApi(
  payload: BulkUpdateOpportunitiesPayload
): Promise<BulkUpdateSubmitResponse> {
  const response = await apiClient.post<
    BulkUpdateOpportunitiesPayload,
    BulkUpdateSubmitResponse
  >("/opportunities/bulk-update", payload);
  return response.data;
}

/** Poll bulk update job status. GET /opportunities/bulk-update/:jobId */
export async function getBulkUpdateJobStatusApi(
  jobId: string
): Promise<BulkUpdateJobStatusResponse> {
  const response = await apiClient.get<BulkUpdateJobStatusResponse>(
    `/opportunities/bulk-update/${encodeURIComponent(jobId)}`
  );
  return response.data;
}

/**
 * Update an opportunity by ID.
 * PATCH /opportunities/:opportunityId
 */
export async function patchOpportunityApi(
  opportunityId: string,
  payload: PatchOpportunityPayload
): Promise<PatchOpportunityResponse> {
  const response = await apiClient.patch<
    PatchOpportunityPayload,
    PatchOpportunityResponse
  >(`/opportunities/${encodeURIComponent(opportunityId)}`, payload);
  return response.data;
}

/**
 * Get opportunity details by ID (unmasked for edit modal prefill - CAR-1260).
 * GET /opportunities/:id
 */
export async function getOpportunityByIdApi(
  opportunityId: string
): Promise<OpportunityDetail | null> {
  const response = await apiClient.get<
    OpportunityDetailResponse | OpportunityDetail
  >(`/opportunities/${encodeURIComponent(opportunityId)}`);
  return unwrapOpportunityDetail(response.data);
}

export const getOpportunitiesApi = async (
  params?: OpportunitiesQueryParams
): Promise<OpportunitiesResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.tab) {
    queryParams.append("tab", params.tab);
  }
  if (params?.page !== undefined && params?.page !== null) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.limit !== undefined && params?.limit !== null) {
    queryParams.append("limit", params.limit.toString());
  }

  // Multi-value: prefer array when present to avoid duplicate keys
  if (params?.typeArray?.length) {
    params.typeArray.forEach((v) => queryParams.append("type", v));
  } else if (params?.type) {
    queryParams.append("type", params.type);
  }
  if (params?.statusArray?.length) {
    params.statusArray.forEach((v) => queryParams.append("status", v));
  } else if (params?.status) {
    queryParams.append("status", params.status);
  }
  if (params?.pipelineStageIdArray?.length) {
    params.pipelineStageIdArray.forEach((v) =>
      queryParams.append("pipelineStageId", v)
    );
  } else if (params?.pipelineStageId) {
    queryParams.append("pipelineStageId", params.pipelineStageId);
  }

  (params?.primaryFacilityId ?? []).forEach((v) =>
    queryParams.append("primaryFacilityId", v)
  );
  (params?.leadSource ?? []).forEach((v) =>
    queryParams.append("leadSource", v)
  );
  (params?.integrationConfigId ?? []).forEach((v) =>
    queryParams.append("integrationConfigId", v)
  );
  expandChannelFilterValuesForApi(params?.sourceChannel ?? []).forEach((v) =>
    queryParams.append("sourceChannel", v)
  );
  (params?.departmentId ?? []).forEach((v) =>
    queryParams.append("departmentId", v)
  );
  (params?.advisedByDoctorId ?? []).forEach((v) =>
    queryParams.append("advisedByDoctorId", v)
  );

  if (params?.connectivityStatus) {
    queryParams.append("connectivityStatus", params.connectivityStatus);
  }
  if (params?.dndBucket !== undefined && params?.dndBucket !== null) {
    queryParams.append("dndBucket", String(params.dndBucket));
  }
  if (params?.registrationStatus) {
    queryParams.append("registrationStatus", params.registrationStatus);
  }
  (params?.payorType ?? []).forEach((v) => {
    queryParams.append("payorType", v);
  });

  const queryString = queryParams.toString();
  const url = queryString ? `leads/my-leads?${queryString}` : "leads/my-leads";
  const response = await apiClient.get<OpportunitiesResponse>(url);
  return response.data;
};

export const searchLeadsApi = async (
  params: SearchLeadsQueryParams
): Promise<OpportunitiesResponse> => {
  const queryParams = new URLSearchParams();

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    queryParams.append("searchQuery", params.searchQuery.trim());
  }

  if (params.page !== undefined && params.page !== null) {
    queryParams.append("page", params.page.toString());
  }
  if (params.limit !== undefined && params.limit !== null) {
    queryParams.append("limit", params.limit.toString());
  }

  // Multi-value filters
  (params.typeArray ?? []).forEach((val) => queryParams.append("type", val));
  (params.source ?? []).forEach((val) => queryParams.append("source", val));
  (params.departmentId ?? []).forEach((val) =>
    queryParams.append("departmentId", val)
  );
  (params.payerType ?? []).forEach((val) =>
    queryParams.append("payerType", val)
  );
  // Only send status when user has selected a status filter; do not send default status
  const hasStatusFilter =
    (params.statusArray && params.statusArray.length > 0) ||
    (params.status && params.status.trim().length > 0);
  if (hasStatusFilter) {
    const statusVal = params.statusArray?.length
      ? params.statusArray.join(",")
      : (params.status ?? "");
    if (statusVal) queryParams.append("status", statusVal);
  }
  (params.pipelineStageIdArray ?? []).forEach((val) =>
    queryParams.append("pipelineStageId", val)
  );
  (params.unitId ?? []).forEach((val) => queryParams.append("unitId", val));
  (params.doctorId ?? []).forEach((val) => queryParams.append("doctorId", val));
  (params.callAttempted ?? []).forEach((val) =>
    queryParams.append("callAttempted", val)
  );

  // Single-value fallbacks
  if (params.type) {
    queryParams.append("type", params.type);
  }

  const queryString = queryParams.toString();
  const url = `leads/search?${queryString}`;
  const response = await apiClient.get<OpportunitiesResponse>(url);
  return response.data;
};

/** Search patients/contacts for quick lookup modal - returns person-level aggregated results */
export const searchPatientsApi = async (
  params: SearchLeadsQueryParams
): Promise<SearchLeadsResponse> => {
  const queryParams = new URLSearchParams();
  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    queryParams.append("searchQuery", params.searchQuery.trim());
  }
  if (params.page !== undefined && params.page !== null) {
    queryParams.append("page", params.page.toString());
  }
  if (params.limit !== undefined && params.limit !== null) {
    queryParams.append("limit", params.limit.toString());
  }
  // Only send status when user has selected a status filter; do not send default status
  const hasStatusFilter =
    (params.statusArray && params.statusArray.length > 0) ||
    (params.status && params.status.trim().length > 0);
  if (hasStatusFilter) {
    const statusVal = params.statusArray?.length
      ? params.statusArray.join(",")
      : (params.status ?? "");
    if (statusVal) queryParams.append("status", statusVal);
  }
  const queryString = queryParams.toString();
  const url = `leads/search?${queryString}`;
  const response = await apiClient.get<SearchLeadsResponse>(url);
  return response.data;
};

/** Universal Search — GET leads/v2/search (cursor pagination, zero-opp people, hisSearch metadata) */
export const searchPatientsV2Api = async (
  params: SearchLeadsV2QueryParams
): Promise<SearchLeadsV2Response> => {
  const queryParams = new URLSearchParams();
  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    queryParams.append("searchQuery", params.searchQuery.trim());
  }
  const limit = params.limit ?? 50;
  queryParams.append("limit", limit.toString());
  if (params.cursor) {
    queryParams.append("cursor", params.cursor);
  }
  const url = `leads/v2/search?${queryParams.toString()}`;
  const response = await apiClient.get<SearchLeadsV2Response>(url);
  return response.data;
};

export const getLeadCountsApi = async (
  params?: LeadCountsParams
): Promise<LeadCountsResponse> => {
  const queryParams = new URLSearchParams();

  if (params) {
    (params.type ?? []).forEach((val) => {
      queryParams.append("type", val);
    });
    (params.status ?? []).forEach((val) => {
      queryParams.append("status", val);
    });
    (params.leadSource ?? []).forEach((val) => {
      queryParams.append("leadSource", val);
    });
    (params.primaryFacilityId ?? []).forEach((val) => {
      queryParams.append("primaryFacilityId", val);
    });
    (params.advisedByDoctorId ?? []).forEach((val) => {
      queryParams.append("advisedByDoctorId", val);
    });
    (params.payorType ?? []).forEach((val) => {
      queryParams.append("payorType", val);
    });
    (params.pipelineStageId ?? []).forEach((val) => {
      queryParams.append("pipelineStageId", val);
    });
    (params.departmentId ?? []).forEach((val) => {
      queryParams.append("departmentId", val);
    });
    expandChannelFilterValuesForApi(params.sourceChannel ?? []).forEach(
      (val) => {
        queryParams.append("sourceChannel", val);
      }
    );
    if (params.registrationStatus) {
      queryParams.append("registrationStatus", params.registrationStatus);
    }
    if (params.connectivityStatus) {
      queryParams.append("connectivityStatus", params.connectivityStatus);
    }
    if (params.dndBucket !== undefined && params.dndBucket !== null) {
      queryParams.append("dndBucket", String(params.dndBucket));
    }
    (params.callAttempted ?? []).forEach((val) => {
      queryParams.append("callAttempted", val);
    });
    (params.caseType ?? []).forEach((val) => {
      queryParams.append("caseType", val);
    });
  }

  const queryString = queryParams.toString();
  const url = queryString ? `leads/counts?${queryString}` : "leads/counts";
  const response = await apiClient.get<LeadCountsResponse>(url);
  return response.data;
};

/**
 * Query params for GET /opportunities/patient/:patientId/list
 */
export interface PatientOpportunityListParams {
  /** Filter by status (e.g. OPEN). Comma-separated for multiple. */
  status?: string;
  /** Filter by type (e.g. DR_CONSULTATION,FOLLOWUP,REFERRAL). Comma-separated for multiple. */
  type?: string;
}

/**
 * Get opportunities list for a patient (for task/work order opportunity select).
 * GET /opportunities/patient/:patientId/list
 * Optional query params: status, type (e.g. status=OPEN&type=DR_CONSULTATION%2CFOLLOWUP%2CREFERRAL).
 */
export async function getPatientOpportunitiesListApi(
  patientId: string,
  params?: PatientOpportunityListParams
): Promise<PatientOpportunityListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append("status", params.status);
  if (params?.type) queryParams.append("type", params.type);
  const queryString = queryParams.toString();
  const url = `/opportunities/patient/${encodeURIComponent(patientId)}/list${queryString ? `?${queryString}` : ""}`;
  const response = await apiClient.get<PatientOpportunityListResponse>(url);
  return response.data;
}

/**
 * Query params for GET /opportunities/contact/:contactId/list
 */
export interface ContactOpportunityListParams {
  /** Filter by status (e.g. OPEN). Comma-separated for multiple. */
  status?: string;
}

/**
 * Get opportunities list for a contact (for task/work order opportunity select).
 * GET /opportunities/contact/:contactId/list
 * Optional query params: status (e.g. status=OPEN).
 */
export async function getContactOpportunitiesListApi(
  contactId: string,
  params?: ContactOpportunityListParams
): Promise<ContactOpportunityListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append("status", params.status);
  const queryString = queryParams.toString();
  const url = `/opportunities/contact/${encodeURIComponent(contactId)}/list${queryString ? `?${queryString}` : ""}`;
  const response = await apiClient.get<ContactOpportunityListResponse>(url);
  return response.data;
}

/**
 * Map a row from GET leads/filter to the existing Opportunity type for table consumption.
 */
export function mapFilterOpportunityRowToOpportunity(
  row: FilterOpportunityRow
): Opportunity {
  return {
    _id: row._id,
    tenantId: "",
    patientId: row.patientId,
    contactId: row.contactId,
    patientUhid: row.patientUhid ?? null,
    opportunityNumber: row.opportunityNumber,
    type: row.type,
    displayName: row.displayName,
    status: row.status,
    stage: row.pipelineStageId,
    pipelineId: "",
    pipelineStageId: row.pipelineStageId,
    outreachStatus: "",
    primaryFacilityId: row.primaryFacilityId,
    departmentId: row.departmentId ?? "",
    departmentName: row.departmentName,
    advisedByDoctorId: row.advisedByDoctorId,
    advisedByDoctorName: row.advisedByDoctorName,
    assignedToUserId: row.assignedToUserId,
    assignedToUserName: row.assignedToUserName,
    leadSource: row.leadSource,
    sourceChannel: displaySourceChannel(row.leadSource, row.sourceChannel),
    utmCampaign: row.utmCampaign ?? undefined,
    integrationConfigId: row.integrationConfigId ?? undefined,
    integrationConfigName: row.integrationConfigName ?? undefined,
    dedupeKey: "",
    expiresAt: null,
    assignmentState: "",
    currencyCode: "",
    callAttemptCount: 0,
    rnrCount: row.rnrCount,
    isDnd: row.isDnd,
    priority: "",
    followUpDate: row.followUpDate ?? null,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt ?? row.createdAt,
    lastActivityAt: row.lastActivityAt ?? null,
    createdAtIST: row.createdAtIST,
    lastActivityAtIST: row.lastActivityAtIST,
    prescriptionDataId: null,
    externalBookingId: null,
    patientName: row.patientName,
    patientPhone: row.patientPhone,
    contactName: row.patientName ?? undefined,
    contactPhone: row.patientPhone ?? undefined,
    payorType: row.payorType,
    payorName: row.payorName,
    pendingCallback: row.pendingCallback,
    caseType: row.caseType,
  };
}

/**
 * Filter opportunities (opportunity-centric, cursor-based).
 * GET leads/filter — for My Leads with filter bar.
 */
export async function filterOpportunitiesApi(
  params: FilterOpportunitiesParams
): Promise<FilterOpportunitiesResponse> {
  const queryParams = new URLSearchParams();

  if (params.status !== undefined && params.status !== "") {
    queryParams.append("status", params.status);
  }

  (params.type ?? []).forEach((val) => {
    queryParams.append("type", val);
  });
  (params.source ?? []).forEach((val) => {
    queryParams.append("source", val);
  });
  (params.primaryFacilityId ?? []).forEach((val) => {
    queryParams.append("primaryFacilityId", val);
  });
  (params.payerType ?? []).forEach((val) => {
    queryParams.append("payerType", val);
  });
  (params.pipelineStageId ?? []).forEach((val) => {
    queryParams.append("pipelineStageId", val);
  });
  (params.departmentId ?? []).forEach((val) => {
    queryParams.append("departmentId", val);
  });
  (params.doctorId ?? []).forEach((val) => {
    queryParams.append("doctorId", val);
  });
  if (params.connectivityStatus) {
    queryParams.append("connectivityStatus", params.connectivityStatus);
  }
  if (params.dndBucket !== undefined && params.dndBucket !== null) {
    queryParams.append("dndBucket", String(params.dndBucket));
  }
  if (params.tab) {
    queryParams.append("tab", params.tab);
  }
  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    queryParams.append("searchQuery", params.searchQuery.trim());
  }
  expandChannelFilterValuesForApi(params.sourceChannel ?? []).forEach((val) => {
    queryParams.append("sourceChannel", val);
  });
  (params.integrationConfigId ?? []).forEach((val) => {
    queryParams.append("integrationConfigId", val);
  });
  (params.assignedToUserId ?? []).forEach((val) => {
    queryParams.append("assignee", val);
  });
  if (params.registrationStatus) {
    queryParams.append("registrationStatus", params.registrationStatus);
  }
  const dateFrom = params.startDate
    ? toApiDateRangeStart(params.startDate)
    : undefined;
  const dateTo = params.endDate ? toApiDateRangeEnd(params.endDate) : undefined;
  if (dateFrom) {
    queryParams.append("dateFrom", dateFrom);
  }
  if (dateTo) {
    queryParams.append("dateTo", dateTo);
  }
  if (params.cursor) {
    queryParams.append("cursor", params.cursor);
  }
  if (params.page !== undefined && params.page !== null) {
    queryParams.append("page", params.page.toString());
  }
  if (params.limit !== undefined && params.limit !== null) {
    queryParams.append("limit", params.limit.toString());
  }
  (params.callAttempted ?? []).forEach((val) => {
    queryParams.append("callAttempted", val);
  });
  (params.caseType ?? []).forEach((val) => {
    queryParams.append("caseType", val);
  });

  const queryString = queryParams.toString();
  const url = queryString ? `leads/filter?${queryString}` : "leads/filter";
  const response = await apiClient.get<FilterOpportunitiesResponse>(url);
  return response.data;
}

/**
 * Get assignees for a patient's opportunities.
 * GET /opportunities/patient/:patientId/assignees
 */
export async function getPatientAssigneesApi(
  patientId: string
): Promise<OpportunityAssigneesResponse> {
  const url = `/opportunities/patient/${encodeURIComponent(patientId)}/assignees`;
  const response = await apiClient.get<OpportunityAssigneesResponse>(url);
  return response.data;
}

/**
 * Get assignees for a contact's opportunities.
 * GET /opportunities/contact/:contactId/assignees
 */
export async function getContactAssigneesApi(
  contactId: string
): Promise<OpportunityAssigneesResponse> {
  const url = `/opportunities/contact/${encodeURIComponent(contactId)}/assignees`;
  const response = await apiClient.get<OpportunityAssigneesResponse>(url);
  return response.data;
}

/**
 * Map API assignees response to unique Assignee[] by assigneeName (same name shown once).
 * Filters out items with null/empty assigneeName.
 */
export function mapAssigneesResponseToUniqueAssignees(
  data: OpportunityAssigneeItem[]
): Assignee[] {
  const seen = new Set<string>();
  return data
    .filter(
      (item): item is OpportunityAssigneeItem & { assigneeName: string } => {
        const name = item.assigneeName?.trim();
        return !!name;
      }
    )
    .filter((item) => {
      const name = item.assigneeName!.trim();
      if (seen.has(name)) return false;
      seen.add(name);
      return true;
    })
    .map((item, index) => ({
      id: item.assigneeId ?? `assignee-${index}`,
      name: item.assigneeName!.trim(),
    }));
}

export interface GetOpportunityTypesParams {
  search?: string;
  isActive?: "true" | "false" | "all";
  isDefault?: "true" | "false";
}

/**
 * Active opportunity types for the tenant (config-driven).
 * GET /opportunities/types
 *
 * Response shape: { types: [{ id, type, label, isDefault, isActive, ... }] }
 * - `type` is the raw API key (used in filter / create payloads)
 * - `label` is the admin-configured display name (may be null for system types)
 */
export async function getOpportunityTypesApi(
  params?: GetOpportunityTypesParams
): Promise<OpportunityTypeItem[]> {
  const queryParams = new URLSearchParams();
  if (params?.search?.trim()) {
    queryParams.append("search", params.search.trim());
  }
  if (params?.isActive) {
    queryParams.append("isActive", params.isActive);
  }
  if (params?.isDefault) {
    queryParams.append("isDefault", params.isDefault);
  }
  const queryString = queryParams.toString();
  const url = queryString
    ? `/opportunities/types?${queryString}`
    : "/opportunities/types";

  const response = await apiClient.get<OpportunityTypesResponse>(url);
  const types = response.data?.types;
  if (!Array.isArray(types)) return [];
  return types
    .map((item) => {
      if (typeof item === "string") {
        return { type: item, label: null } as OpportunityTypeItem;
      }
      if (item && typeof item === "object" && typeof item.type === "string") {
        const raw = item as OpportunityTypeItem & { _id?: string };
        const id =
          typeof raw.id === "string"
            ? raw.id
            : typeof raw._id === "string"
              ? raw._id
              : undefined;
        return {
          id,
          type: raw.type,
          label: typeof raw.label === "string" ? raw.label : null,
          isDefault:
            typeof raw.isDefault === "boolean" ? raw.isDefault : undefined,
          isActive:
            typeof raw.isActive === "boolean" ? raw.isActive : undefined,
          expiryHours:
            typeof raw.expiryHours === "number" ? raw.expiryHours : null,
        } as OpportunityTypeItem;
      }
      return null;
    })
    .filter((item): item is OpportunityTypeItem => item !== null);
}

/**
 * Enable a disabled opportunity type config.
 * PATCH /admin/opportunity-config/:id/enable
 */
export async function enableOpportunityConfigApi(
  id: string
): Promise<Record<string, unknown>> {
  const response = await apiClient.patch<undefined, Record<string, unknown>>(
    `/admin/opportunity-config/${encodeURIComponent(id)}/enable`
  );
  return response.data;
}

/**
 * Disable an active opportunity type config.
 * PATCH /admin/opportunity-config/:id/disable
 */
export async function disableOpportunityConfigApi(
  id: string
): Promise<Record<string, unknown>> {
  const response = await apiClient.patch<undefined, Record<string, unknown>>(
    `/admin/opportunity-config/${encodeURIComponent(id)}/disable`
  );
  return response.data;
}

export interface CreateOpportunityTypePayload {
  type: string;
  label?: string;
}

export interface CreateOpportunityTypeResponse {
  type: string;
  label: string | null;
  stages: string[];
  dispositions: string[];
  pipelineId: string;
}

/**
 * Create a fully configured custom opportunity type.
 * POST /admin/opportunity-config/types
 */
export async function createOpportunityTypeApi(
  payload: CreateOpportunityTypePayload
): Promise<CreateOpportunityTypeResponse> {
  const response = await apiClient.post<
    CreateOpportunityTypePayload,
    CreateOpportunityTypeResponse
  >("/admin/opportunity-config/types", payload);
  return response.data;
}
